import { useCallback, useMemo } from 'react'
import { useAgentModelCatalog } from './useAgentModelCatalog'
import { useChatAgentOptions, useUpdateChatAgentSelection } from './useChats'
import { CHAT_AGENT_PROVIDERS } from '@/types'
import type { Chat, ChatAgentProviderOption, ChatAgentSelection } from '@/types'

function preferredEffort(efforts: string[], ...candidates: (string | null | undefined)[]): string | null {
  const supported = candidates.find((candidate) => candidate && efforts.includes(candidate))
  if (supported) return supported
  return efforts.includes('medium') ? 'medium' : efforts[0] ?? null
}

function providerDefault(option: ChatAgentProviderOption): ChatAgentSelection {
  return { agentProvider: option.provider, model: option.defaultModel ?? null, effort: option.defaultEffort ?? null }
}

export function useChatAgentSelection(projectId: string, chatId: string | null, chat: Chat | undefined) {
  const { data, isLoading, isError } = useChatAgentOptions(projectId)
  const providers = useMemo(() => data?.providers ?? [], [data])
  const { mutate: saveSelection } = useUpdateChatAgentSelection(projectId)

  const requested = useMemo<ChatAgentSelection | null>(
    () => (chat?.agentProviderName ? { agentProvider: chat.agentProviderName, model: chat.model, effort: chat.effort } : null),
    [chat],
  )

  const providerOption = providers.find((p) => p.provider === requested?.agentProvider) ?? providers[0]
  const catalog = useAgentModelCatalog(providerOption?.provider ?? '')

  const selection = useMemo<ChatAgentSelection | null>(() => {
    if (!providerOption) return null
    if (requested?.agentProvider !== providerOption.provider) return providerDefault(providerOption)
    if (!providerOption.supportsModelSelection) return { agentProvider: providerOption.provider, model: null, effort: null }
    if (catalog.isLoading || catalog.models.length === 0) return requested

    const model = requested.model && catalog.models.some((m) => m.id === requested.model)
      ? requested.model
      : providerOption.defaultModel ?? catalog.models[0]?.id ?? null
    const efforts = model ? catalog.effortsFor(model) : []
    const effort = efforts.length === 0 ? null : preferredEffort(efforts, requested.effort, providerOption.defaultEffort)

    return { agentProvider: providerOption.provider, model, effort }
  }, [providerOption, requested, catalog])

  const isChatResolved = !chatId || !!chat
  const isReady = !isLoading && isChatResolved

  const persist = useCallback((next: ChatAgentSelection) => {
    if (chatId && chat) saveSelection({ chatId, selection: next })
  }, [chatId, chat, saveSelection])

  const selectProvider = useCallback((provider: string) => {
    const option = providers.find((p) => p.provider === provider)
    if (option) persist(providerDefault(option))
  }, [providers, persist])

  const selectModel = useCallback((model: string) => {
    if (!selection) return
    const efforts = catalog.effortsFor(model)
    persist({ ...selection, model, effort: efforts.length === 0 ? null : preferredEffort(efforts, selection.effort, providerOption?.defaultEffort) })
  }, [selection, catalog, providerOption, persist])

  const selectEffort = useCallback((effort: string) => {
    if (selection) persist({ ...selection, effort })
  }, [selection, persist])

  return {
    providers,
    providerOption,
    selection,
    selectionToSend: isError || !chat ? null : selection,
    forcesContainer: selection?.agentProvider === CHAT_AGENT_PROVIDERS.titan,
    isLoading,
    isError,
    isReady,
    models: catalog.models,
    effortsFor: catalog.effortsFor,
    modelLabel: catalog.modelLabel,
    effortLabel: catalog.effortLabel,
    selectProvider,
    selectModel,
    selectEffort,
  }
}

export type ChatAgentSelectionState = ReturnType<typeof useChatAgentSelection>
